import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_LIVE_TRANSFERS } from '../../../lib/graphql/queries/organizations';

export interface GQLTransferSummary {
  transferId: string;
  transferType?: string;
  status?: string;
  transferReason?: string;
  targetAgentId?: string;
  targetAgentNumber?: string;
  fromNumber?: string;
  toNumber?: string;
  connectedAt?: string;
  completedAt?: string;
  failureReason?: string;
  recordingUrl?: string;
  voicemailUrl?: string;
  initiatedAt?: string;
  createdAt?: string;
  answered?: boolean | null;
  acceptedBy?: string | null;
  transferSuccess?: boolean | null;
}

export interface GQLAssignedAgent {
  agentId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
}

export interface GQLLiveTransferCall {
  callId: string;
  contactId?: string;
  contactName?: string;
  assignedAgentId?: string | null;
  assignedAgent?: GQLAssignedAgent | null;
  fromNumber?: string;
  toNumber?: string;
  inbound?: boolean;
  answeredBy?: string;
  durationSeconds?: number;
  initiatedAt?: string;
  recordingUrl?: string;
  transcript?: string;
  summary?: string;
  flowName?: string;
  flowType?: string;
  outcome?: string | null;
  status?: string | null;
  isTest?: boolean | null;
  /** JSON string: Array<{ event, timestamp }>. Parse client-side. */
  callAnalysisTimeline?: string | null;
  /** JSON string: Record<string, string>. Parse client-side. */
  callAnalysisAnswers?: string | null;
  /** False when the originating call couldn't be linked (SPEC-2172) —
   * call-derived fields above are absent for those transfers. */
  hasLinkedCall?: boolean;
  transfer: GQLTransferSummary;
}

interface GQLLinkedCall {
  callId: string;
  direction?: string;
  inbound?: boolean;
  fromNumber?: string;
  toNumber?: string;
  status?: string;
  outcome?: string | null;
  answeredBy?: string;
  durationSeconds?: number;
  initiatedAt?: string;
  endedAt?: string;
  summary?: string;
  transcript?: string;
  sentiment?: string;
  recordingUrl?: string;
  flowName?: string;
  flowType?: string;
  isTest?: boolean | null;
  callAnalysisTimeline?: string | null;
  callAnalysisAnswers?: string | null;
  createdAt?: string;
}

interface GQLLiveTransferEntry {
  contactId?: string;
  contactName?: string;
  contactPhone?: string;
  assignedAgentId?: string | null;
  assignedAgent?: GQLAssignedAgent | null;
  transfer: GQLTransferSummary;
  call?: GQLLinkedCall | null;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

// SPEC-2172: flatten a LiveTransfers-primary entry into the call-shaped
// object the widget/modal already consume. Call-derived fields fall back to
// transfer/contact data when the originating call couldn't be linked.
const toLiveTransferCall = (entry: GQLLiveTransferEntry): GQLLiveTransferCall => {
  const { transfer, call } = entry;
  return {
    callId: call?.callId ?? transfer.transferId,
    contactId: entry.contactId,
    contactName: entry.contactName,
    assignedAgentId: entry.assignedAgentId,
    assignedAgent: entry.assignedAgent,
    fromNumber: call?.fromNumber ?? transfer.fromNumber,
    toNumber: call?.toNumber ?? entry.contactPhone ?? transfer.toNumber,
    inbound: call?.inbound,
    answeredBy: call?.answeredBy,
    durationSeconds: call?.durationSeconds,
    initiatedAt: call?.initiatedAt ?? transfer.initiatedAt,
    recordingUrl: call?.recordingUrl,
    transcript: call?.transcript,
    summary: call?.summary,
    flowName: call?.flowName,
    flowType: call?.flowType,
    outcome: call?.outcome,
    status: call?.status,
    isTest: call?.isTest,
    callAnalysisTimeline: call?.callAnalysisTimeline,
    callAnalysisAnswers: call?.callAnalysisAnswers,
    hasLinkedCall: !!call,
    transfer,
  };
};

export const useOrgLiveTransfersGQL = ({
  dateRange,
  enabled = true,
  pageSize = 25,
  sortBy = 'initiatedAt',
  sortOrder = 'DESC',
  contactId,
}: {
  dateRange: DateRange;
  enabled?: boolean;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  contactId?: string;
}) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [currentSearch, setCurrentSearch] = useState<string | undefined>(undefined);
  // Modal-level overrides — when set, replace the inherited dateRange.
  const [currentDateFrom, setCurrentDateFrom] = useState<string | undefined>(undefined);
  const [currentDateTo, setCurrentDateTo] = useState<string | undefined>(undefined);

  const dateFrom = currentDateFrom ?? dateRange.from?.toISOString() ?? undefined;
  const dateTo = currentDateTo ?? dateRange.to?.toISOString() ?? undefined;

  const { data, loading } = useQuery(GET_ORG_LIVE_TRANSFERS, {
    variables: {
      organizationId: orgId,
      limit: currentPageSize,
      offset: currentPage * currentPageSize,
      dateFrom,
      dateTo,
      sortBy,
      sortOrder,
      ...(currentSearch ? { search: currentSearch } : {}),
      ...(contactId ? { contactId } : {}),
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  type OrgLiveTransfersData = {
    organization?: {
      liveTransfers?: { items?: GQLLiveTransferEntry[]; total?: number; hasMore?: boolean };
    };
  };
  const d = data as OrgLiveTransfersData;
  const items = d?.organization?.liveTransfers?.items ?? [];
  const transfers = items.map(toLiveTransferCall);
  const total = d?.organization?.liveTransfers?.total ?? 0;
  const hasMore = d?.organization?.liveTransfers?.hasMore ?? false;

  const loadMore = useCallback(() => {
    setCurrentPage((p) => p + 1);
  }, []);

  const refresh = useCallback(() => {
    setCurrentPage(0);
  }, []);

  const loadPage = useCallback(
    (newOffset: number, newLimit: number) => {
      if (newLimit !== currentPageSize) {
        setCurrentPageSize(newLimit);
        setCurrentPage(0);
      } else {
        setCurrentPage(Math.floor(newOffset / newLimit));
      }
    },
    [currentPageSize]
  );

  const filter = useCallback(
    (params: {
      search?: string;
      answeredBy?: string;
      flowName?: string;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      // answeredBy/flowName are part of the shared modal-filter signature but
      // have no LT-widget UI (see LiveTransfersModal) — only search + dates apply.
      setCurrentSearch(params.search);
      setCurrentDateFrom(params.dateFrom);
      setCurrentDateTo(params.dateTo);
      setCurrentPage(0);
    },
    []
  );

  return {
    transfers,
    total,
    hasMore,
    isLoading: loading,
    loadMore,
    loadPage,
    refresh,
    filter,
  };
};
